import { useState } from "react";
import { Check, Star } from "lucide-react";
import { formatParisDate } from "@/lib/parisDateTime";
export type SurveyContext = {
  firstName: string | null;
  center: string | null;
  eventDate: string;
  activities: string[];
  expressSaved: boolean;
  detailsSaved: boolean;
};
export type ExpressAnswers = {
  overall: number;
  sales: number;
  venue: number;
  nps: number;
  activities: string[];
  cross_selling_optin: boolean;
};
export type DetailAnswers = {
  catering: number | null;
  catering_na: boolean;
  improvement: string;
  testimonial_consent: boolean;
};
export function Rating({
  label,
  max,
  value,
  onChange,
}: {
  label: string;
  max: number;
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium leading-relaxed">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: max === 10 ? 11 : 5 }, (_, i) => (max === 10 ? i : i + 1)).map(
          (n) => (
            <label
              key={n}
              className={`flex min-h-11 min-w-11 cursor-pointer items-center justify-center gap-1 rounded-lg border px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2 ${value === n ? "border-[#18182D] bg-[#18182D] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"}`}
            >
              <input
                type="radio"
                required
                checked={value === n}
                onChange={() => onChange(n)}
                name={label}
                value={n}
                className="sr-only"
              />
              <span>{n}</span>
              {max === 5 && <Star size={13} fill="currentColor" />}
            </label>
          ),
        )}
      </div>
      <div className="flex justify-between text-[11px] text-gray-400">
        <span>{max === 10 ? "Pas du tout probable" : "Très insatisfait"}</span>
        <span>{max === 10 ? "Tout à fait probable" : "Très satisfait"}</span>
      </div>
    </fieldset>
  );
}
export function FeedbackSurvey({
  context,
  initialScore = null,
  onExpress,
  onDetails,
  demo = false,
}: {
  context: SurveyContext;
  initialScore?: number | null;
  onExpress: (a: ExpressAnswers) => Promise<void>;
  onDetails: (a: DetailAnswers) => Promise<void>;
  demo?: boolean;
}) {
  const [overall, setOverall] = useState<number | null>(initialScore);
  const [sales, setSales] = useState<number | null>(null);
  const [venue, setVenue] = useState<number | null>(null);
  const [nps, setNps] = useState<number | null>(null);
  const [activities, setActivities] = useState<string[]>([]);
  const [optin, setOptin] = useState(false);
  const [saved, setSaved] = useState(context.expressSaved);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [done, setDone] = useState(context.detailsSaved);
  const [catering, setCatering] = useState<number | null>(null);
  const [cateringNa, setCateringNa] = useState(false);
  const [improvement, setImprovement] = useState("");
  const [testimonial, setTestimonial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (overall == null || sales == null || venue == null || nps == null) return;
    setBusy(true);
    setError("");
    try {
      await onExpress({ overall, sales, venue, nps, activities, cross_selling_optin: optin });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Votre avis n’a pas pu être enregistré.");
    } finally {
      setBusy(false);
    }
  };
  const submitDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onDetails({
        catering,
        catering_na: cateringNa,
        improvement,
        testimonial_consent: testimonial,
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ces précisions n’ont pas pu être enregistrées.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#EFEEF3] px-5 py-10 text-[#18182D]">
      {demo && (
        <p className="mx-auto mb-4 max-w-xl rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-center text-sm text-indigo-900">
          Formulaire de démonstration · Réponses fictives uniquement
        </p>
      )}
      <main className="mx-auto max-w-xl overflow-hidden rounded-2xl border border-[#18182D]/10 bg-white">
        <header className="bg-[#18182D] px-7 py-7 text-white">
          <p className="text-sm font-semibold tracking-wide">Smile World</p>
          <h1 className="mt-5 text-3xl font-semibold tracking-tight">Votre expérience compte.</h1>
          <p className="mt-3 text-sm leading-relaxed text-[#DEDEFF]">
            {context.firstName ? `Merci ${context.firstName},` : "Merci"} de nous avoir confié votre
            événement{context.center ? ` à ${context.center}` : ""}
            {context.eventDate ? ` le ${formatParisDate(context.eventDate)}` : ""}.
          </p>
        </header>
        <div className="p-7">
          {!saved ? (
            <form onSubmit={submit} className="space-y-7">
              <p className="text-sm text-gray-500">
                Quelques questions pour nous aider à faire encore mieux.
              </p>
              <Rating
                label="Comment s’est passée votre expérience globale ?"
                max={5}
                value={overall}
                onChange={setOverall}
              />
              <Rating
                label="Comment évaluez-vous la réactivité et les conseils de votre interlocuteur commercial ?"
                max={5}
                value={sales}
                onChange={setSales}
              />
              <Rating
                label="L’accueil, l’animation et l’ambiance au centre ont-ils plu à votre groupe ?"
                max={5}
                value={venue}
                onChange={setVenue}
              />
              <Rating
                label="Recommanderiez-vous Smile World à un autre organisateur d’événement ?"
                max={10}
                value={nps}
                onChange={setNps}
              />
              {context.activities.length > 0 && (
                <fieldset>
                  <legend className="mb-3 text-sm font-medium">
                    Quelles activités avez-vous particulièrement appréciées ?
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {context.activities.map((a) => (
                      <label
                        key={a}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm ${activities.includes(a) ? "border-indigo-300 bg-indigo-50" : "border-gray-200"}`}
                      >
                        <input
                          type="checkbox"
                          checked={activities.includes(a)}
                          onChange={(e) =>
                            setActivities((list) =>
                              e.target.checked ? [...list, a] : list.filter((v) => v !== a),
                            )
                          }
                          className="accent-[#18182D]"
                        />
                        {a}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              <div className="rounded-xl bg-[#F4F3FA] p-4">
                <p className="text-sm font-medium">Envie de découvrir d’autres centres ?</p>
                <label className="mt-3 flex items-start gap-2 text-sm leading-relaxed">
                  <input
                    type="checkbox"
                    checked={optin}
                    onChange={(e) => setOptin(e.target.checked)}
                    className="mt-1 accent-[#18182D]"
                  />
                  Je souhaite recevoir des informations sur les autres centres Smile World pour un
                  prochain événement.
                </label>
              </div>
              <p className="text-xs leading-relaxed text-gray-400">
                Vos réponses sont transmises à notre équipe. La demande d’informations ci-dessus est
                facultative.
              </p>
              {error && (
                <p role="alert" className="text-sm text-red-700">
                  {error}
                </p>
              )}
              <button
                disabled={busy}
                className="w-full rounded-xl bg-[#18182D] p-3.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Enregistrement…" : "Valider mon avis"}
              </button>
            </form>
          ) : (
            <>
              <div className="mb-6 rounded-xl border border-emerald-100 bg-emerald-50 p-5">
                <Check className="mb-2 text-emerald-700" />
                <h2 className="text-xl font-semibold text-emerald-900">
                  Merci, votre avis est enregistré.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-emerald-800">
                  {done
                    ? "Vos précisions ont également été enregistrées. À bientôt chez Smile World !"
                    : "Vous pouvez fermer cette page, ou nous partager quelques précisions si vous le souhaitez."}
                </p>
              </div>
              {!done && !detailsOpen && (
                <button
                  onClick={() => setDetailsOpen(true)}
                  className="text-sm font-medium underline underline-offset-4"
                >
                  Ajouter des précisions (facultatif)
                </button>
              )}
              {!done && detailsOpen && (
                <form onSubmit={submitDetails} className="space-y-6">
                  <h2 className="text-lg font-semibold">Pour aller un peu plus loin</h2>
                  {!cateringNa && (
                    <fieldset>
                      <legend className="mb-3 text-sm font-medium">
                        La restauration et les boissons
                      </legend>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            type="button"
                            key={n}
                            aria-pressed={catering === n}
                            onClick={() => setCatering(catering === n ? null : n)}
                            className={`rounded-lg border px-4 py-2 ${catering === n ? "border-[#18182D] bg-[#18182D] text-white" : "border-gray-200"}`}
                          >
                            {n} ★
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={cateringNa}
                      onChange={(e) => {
                        setCateringNa(e.target.checked);
                        if (e.target.checked) setCatering(null);
                      }}
                    />
                    Non concerné par la restauration ou les boissons
                  </label>
                  <label className="block text-sm font-medium">
                    Un point à améliorer pour une prochaine fois ?
                    <textarea
                      value={improvement}
                      maxLength={5000}
                      onChange={(e) => setImprovement(e.target.value)}
                      rows={4}
                      className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm font-normal"
                    />
                  </label>
                  <label className="flex items-start gap-2 text-sm leading-relaxed">
                    <input
                      type="checkbox"
                      checked={testimonial}
                      onChange={(e) => setTestimonial(e.target.checked)}
                      className="mt-1"
                    />
                    J’autorise Smile World à utiliser mon témoignage sur son site web. Cette
                    autorisation est facultative.
                  </label>
                  {error && (
                    <p role="alert" className="text-sm text-red-700">
                      {error} Votre avis initial reste enregistré.
                    </p>
                  )}
                  <button
                    disabled={busy}
                    className="w-full rounded-xl bg-[#18182D] p-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {busy ? "Enregistrement…" : "Enregistrer mes précisions"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
